import axios, { type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';
import type { LoginParams, AuthResponse } from '@app-types/models/user';

const API_URL = import.meta.env.DEV ? 'http://localhost:3005/api' : import.meta.env.VITE_API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!config.headers.Authorization) {
    const impersonationToken = sessionStorage.getItem('impersonation_token');
    const normalToken = localStorage.getItem('token');
    const token = impersonationToken || normalToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
}, (error: unknown) => {
  console.error('Request interceptor error:', error);
  return Promise.reject(error);
});

api.interceptors.response.use((response: AxiosResponse) => {
  return response;
}, (error: unknown) => {
  const axiosError = error as { response?: { status?: number; data?: unknown }; message?: string };
  console.error('API error:', axiosError.response?.status, axiosError.response?.data, axiosError.message);
  if (axiosError.response?.status === 403 || axiosError.response?.status === 401) {
    const isImpersonating = !!sessionStorage.getItem('impersonation_token');
    if (!isImpersonating) {
      // Expired/invalid session: wipe ALL auth state and send the user to the
      // login page. Previously this only cleared god-mode sessionStorage and
      // left the app stuck — pages kept erroring (e.g. "Couldn't load your
      // profile") with no path back to login once the 30m/7d JWT expired.
      sessionStorage.removeItem('impersonation_token');
      sessionStorage.removeItem('impersonated_user');
      sessionStorage.removeItem('godmode_original_user');
      sessionStorage.removeItem('godmode_original_token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      const currentPath = window.location.pathname;
      if (currentPath !== '/' && currentPath !== '/login') {
        sessionStorage.setItem('intendedPath', currentPath);
      }
      // Hard navigate (not navigate()) so every in-flight page unmounts and
      // the app re-initialises cleanly at login.
      window.location.href = '/';
    }
  }
  return Promise.reject(error);
});

export type FormDataValue = string | number | boolean | File | Blob | null | undefined | FormDataValue[] | { [key: string]: FormDataValue };

export const objectToFormData = (obj: Record<string, FormDataValue>, form: FormData = new FormData(), namespace = ''): FormData => {
  for (const property in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, property)) {
      const formKey = namespace ? `${namespace}[${property}]` : property;
      const value = obj[property];

      if (value !== null && value !== undefined && typeof value === 'object' && !(value instanceof File) && !(value instanceof Blob)) {
        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            const itemKey = `${formKey}[${index}]`;
            if (typeof item === 'object' && item !== null && !(item instanceof File) && !(item instanceof Blob)) {
              objectToFormData(item as Record<string, FormDataValue>, form, itemKey);
            } else {
              if (item !== null && item !== undefined) {
                form.append(itemKey, item as string | Blob);
              }
            }
          });
        } else {
          objectToFormData(value as Record<string, FormDataValue>, form, formKey);
        }
      } else {
        if (value !== null && value !== undefined) {
          form.append(formKey, value as string | Blob);
        }
      }
    }
  }
  return form;
};

export const registerStylist = async (data: Record<string, unknown>): Promise<AxiosResponse<AuthResponse>> => {
  const response = await api.post<AuthResponse>('/auth/register-stylist', data);
  sessionStorage.removeItem('impersonation_token');
  sessionStorage.removeItem('impersonated_user');
  sessionStorage.removeItem('godmode_original_user');
  sessionStorage.removeItem('godmode_original_token');
  localStorage.setItem('token', response.data.token);
  localStorage.setItem('user', JSON.stringify(response.data.user));
  console.log('Login successful:', response.data);
  return response;
};

export const login = async (params: LoginParams): Promise<AxiosResponse<AuthResponse>> => {
  try {
    const response = await api.post<AuthResponse>('/auth/business', params);
    sessionStorage.removeItem('impersonation_token');
    sessionStorage.removeItem('impersonated_user');
    sessionStorage.removeItem('godmode_original_user');
    sessionStorage.removeItem('godmode_original_token');
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    console.log('Login successful:', response.data);
    return response;
  } catch (error) {
    const err = error as Error;
    console.error('Login failed:', err.message);
    throw error;
  }
};

interface PlatformGrowthData {
  numberOfStylists: number;
  stylistGrowthSinceLastWeek?: number;
  numberOfUsers?: number;
  userGrowthSinceLastWeek?: number;
}

export const getPlatformGrowth = async (): Promise<PlatformGrowthData> => {
  try {
    const response = await api.get<PlatformGrowthData>('/platform-growth');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch platform growth data:', error);
    throw error;
  }
};

export default api;
